import { useEffect, useMemo, useRef, useState } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Plus, PencilSimple, Trash, Tag, Calendar, CaretLeft, CaretRight, ChartBar, WashingMachine, Fan, Coffee, Paperclip, FileText, Sparkle, EnvelopeSimple, CheckCircle, XCircle, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import RecurringExpenses from "@/components/RecurringExpenses";
import ReceiptScanDialog from "@/components/ReceiptScanDialog";

const todayIso = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,"0")}-${String(t.getDate()).padStart(2,"0")}`;
};
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(+y, +mo-1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
};
const CAT_COLORS = {
  Lebensmittel:      "bg-emerald-100 text-emerald-800",
  Reinigung:         "bg-sky-100 text-sky-800",
  Reparaturen:       "bg-amber-100 text-amber-800",
  Energie:           "bg-red-100 text-red-800",
  Verbrauchsmaterial:"bg-violet-100 text-violet-800",
  Marketing:         "bg-pink-100 text-pink-800",
  Versicherung:      "bg-slate-100 text-slate-800",
  Mobiliar:          "bg-teal-100 text-teal-800",
  Mitarbeiter:       "bg-orange-100 text-orange-800",
  Equipment:         "bg-blue-100 text-blue-800",
  Sonstiges:         "bg-slate-100 text-slate-700",
};
const catCls = (c) => CAT_COLORS[c] || "bg-indigo-100 text-indigo-800";
const inpCls = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function ExpenseDialog({ value, categories, onClose, onSaved }) {
  const [d, setD] = useState(value);
  const [busy, setBusy] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const filtered = (categories || []).filter(c => (d.category || "").length && c.toLowerCase().includes((d.category || "").toLowerCase()) && c !== d.category).slice(0, 6);

  const save = async () => {
    if (!d.date || !d.purpose || !d.amount) { toast.error("Bitte alle Pflichtfelder ausfüllen."); return; }
    setBusy(true);
    try {
      const payload = { date: d.date, purpose: d.purpose, amount: parseFloat(d.amount), category: d.category || "Sonstiges", notes: d.notes || "", haus_geteilt: !!d.haus_geteilt };
      let saved;
      if (d.id) saved = (await api.put(`/expenses/${d.id}`, payload)).data;
      else      saved = (await api.post("/expenses", payload)).data;
      // Optional: Beleg hochladen
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        try {
          await api.post(`/expenses/${saved.id || d.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" }});
        } catch (e) { toast.error("Ausgabe gespeichert, aber Beleg-Upload fehlgeschlagen: " + (e.response?.data?.detail || "Fehler")); }
      }
      toast.success("Ausgabe gespeichert");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const removeReceipt = async () => {
    if (!d.id || !d.receipt_file) return;
    if (!window.confirm("Beleg wirklich entfernen?")) return;
    try { await api.delete(`/expenses/${d.id}/receipt`); setD(x=>({ ...x, receipt_file: null })); toast.success("Beleg entfernt"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <Dialog open={true} onOpenChange={(o)=>{if(!o) onClose();}}>
      <DialogContent className="max-w-md" data-testid="expense-dialog">
        <DialogHeader><DialogTitle>{d.id ? "Ausgabe bearbeiten" : "Neue Ausgabe"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Datum *</Label>
            <Input type="date" value={d.date} onChange={(e)=>set("date", e.target.value)} className={`${inpCls} mt-1`} data-testid="exp-date"/>
          </div>
          <div>
            <Label className="text-xs">Zweck *</Label>
            <Input value={d.purpose} onChange={(e)=>set("purpose", e.target.value)} className={`${inpCls} mt-1`} placeholder="z. B. Wochenendeinkauf REWE" data-testid="exp-purpose"/>
          </div>
          <div>
            <Label className="text-xs">Betrag * (EUR)</Label>
            <Input type="number" step="0.01" min="0" value={d.amount} onChange={(e)=>set("amount", e.target.value)} className={`${inpCls} mt-1`} placeholder="0,00" data-testid="exp-amount"/>
          </div>
          <div className="relative">
            <Label className="text-xs">Kategorie *</Label>
            <Input
              value={d.category}
              onChange={(e)=>set("category", e.target.value)}
              list="expense-categories-datalist"
              className={`${inpCls} mt-1`}
              placeholder="z. B. Lebensmittel"
              data-testid="exp-category"
            />
            <datalist id="expense-categories-datalist">
              {(categories || []).map(c => <option key={c} value={c}/>)}
            </datalist>
            {filtered.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {filtered.map(c => (
                  <button key={c} type="button" onClick={()=>set("category", c)}
                          className={`text-xs px-2 py-0.5 rounded-full ${catCls(c)} hover:opacity-80`}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Notiz (optional)</Label>
            <Textarea value={d.notes} onChange={(e)=>set("notes", e.target.value)} rows={2} className="rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 mt-1"/>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition" data-testid="exp-haus-geteilt-label">
              <input
                type="checkbox"
                checked={!!d.haus_geteilt}
                onChange={(e)=>set("haus_geteilt", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-600"
                data-testid="exp-haus-geteilt"
              />
              <div>
                <div className="font-semibold text-sm text-amber-900">2/3 anteilig (Haus-Gemeinkosten)</div>
                <div className="text-[11px] text-amber-800 leading-snug">Für Rechnungen, die das gesamte Haus betreffen (Dach, Heizung, Allgemein-Strom). Nur 2 von 3 Wohnungen sind vermietet → 2/3 absetzbar.</div>
              </div>
            </label>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <Label className="text-xs flex items-center gap-1.5"><Paperclip size={12} weight="bold"/> Beleg (optional, JPG/PNG/PDF · max 5 MB)</Label>
            {d.receipt_file ? (
              <div className="mt-1.5 flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm">
                <FileText size={16} weight="duotone" className="text-emerald-700"/>
                <a href={`${API_BASE}/api/expenses/${d.id}/receipt`} target="_blank" rel="noopener noreferrer" className="flex-1 text-emerald-800 font-semibold hover:underline truncate" data-testid="exp-receipt-open">
                  Beleg ansehen
                </a>
                <button type="button" onClick={removeReceipt} className="text-red-600 hover:bg-red-100 rounded p-1" data-testid="exp-receipt-del">
                  <Trash size={14} weight="bold"/>
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e)=>setReceiptFile(e.target.files?.[0] || null)}
                className="mt-1.5 text-xs file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-semibold hover:file:bg-slate-200"
                data-testid="exp-receipt-input"
              />
            )}
            {receiptFile && !d.receipt_file && (
              <div className="mt-1 text-[11px] text-emerald-700">Ausgewählt: {receiptFile.name} ({Math.round(receiptFile.size/1024)} KB) — wird beim Speichern hochgeladen</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg" disabled={busy}>Abbrechen</Button>
          <Button onClick={save} disabled={busy} className="rounded-lg bg-primary text-white" data-testid="exp-save">Speichern</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Ausgaben() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [yearly, setYearly] = useState(null);
  const [washStats, setWashStats] = useState(null);
  const [edit, setEdit] = useState(null);
  const [showScan, setShowScan] = useState(false);

  const year = month.slice(0, 4);

  const load = () => {
    api.get("/expenses", { params: { month } }).then(r => setRows(r.data));
    api.get("/expenses/summary", { params: { month } }).then(r => setSummary(r.data));
    api.get("/expenses/categories").then(r => setCategories(r.data.categories));
    api.get("/expenses/monthly", { params: { year } }).then(r => setYearly(r.data));
    api.get("/wash-counter/stats", { params: { month } }).then(r => setWashStats(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`);
  };

  const [washBusy, setWashBusy] = useState(null);
  const washClick = async (device) => {
    setWashBusy(device);
    try {
      const r = await api.post("/wash-counter/click", { device });
      const lab = { wash: "Waschmaschine", dryer: "Trockner", espresso: "Espresso" }[device];
      const c = r.data.cost || 0;
      toast.success(c > 0 ? `${lab} +1 · ${euro(c)} verbucht` : `${lab} +1 (kein Betrag hinterlegt)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setWashBusy(null); }
  };
  const washUndo = async (device) => {
    setWashBusy(device);
    try {
      const r = await api.post("/wash-counter/undo", { device });
      const lab = { wash: "Waschmaschine", dryer: "Trockner", espresso: "Espresso" }[device];
      const c = r.data.cost || 0;
      toast.success(c > 0 ? `${lab} −1 · ${euro(c)} storniert` : `${lab} −1 · Klick zurückgenommen`, { duration: 2500 });
      load();
    } catch (e) {
      if (e.response?.status === 404) toast.info(e.response.data.detail);
      else toast.error(e.response?.data?.detail || "Fehler");
    } finally { setWashBusy(null); }
  };
  const washHoldRef = useRef({ timer: null, longPressed: false });
  const startHold = (device) => {
    washHoldRef.current.longPressed = false;
    washHoldRef.current.timer = setTimeout(() => {
      washHoldRef.current.longPressed = true;
      washUndo(device);
    }, 550);
  };
  const cancelHold = () => {
    if (washHoldRef.current.timer) { clearTimeout(washHoldRef.current.timer); washHoldRef.current.timer = null; }
  };
  const onQuickClick = (device) => {
    if (washHoldRef.current.longPressed) { washHoldRef.current.longPressed = false; return; }
    washClick(device);
  };
  const onQuickContext = (device, e) => { e.preventDefault(); cancelHold(); washUndo(device); };

  const openNew = () => setEdit({ date: todayIso(), purpose: "", amount: "", category: "", notes: "" });
  const openEdit = (e) => setEdit({ ...e });
  const del = async (id) => {
    if (!window.confirm("Diese Ausgabe wirklich löschen?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  // KI-Entwürfe vom Rest trennen
  const drafts = (rows || []).filter(r => r.is_draft);
  const finalRows = (rows || []).filter(r => !r.is_draft);

  const confirmDraft = async (id) => {
    try {
      await api.post(`/expenses/${id}/confirm-draft`);
      toast.success("Entwurf bestätigt");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const rejectDraft = async (id) => {
    if (!window.confirm("Entwurf verwerfen? Die Ausgabe wird gelöscht.")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Entwurf verworfen");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const total = summary?.total || 0;

  return (
    <div className="space-y-6" data-testid="ausgaben-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center">
            <Receipt size={22} weight="duotone" color="white"/>
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily:"'Work Sans',sans-serif"}}>Ausgaben</h1>
            <p className="text-muted-foreground text-sm mt-1">Betriebs­ausgaben erfassen und nach Monat auswerten</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>shiftMonth(-1)} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" title="Vorheriger Monat" data-testid="exp-prev-month">
            <CaretLeft size={16} weight="bold"/>
          </button>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="h-10 rounded-lg w-40" data-testid="exp-month"/>
          <button onClick={()=>shiftMonth(1)} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50" title="Nächster Monat" data-testid="exp-next-month">
            <CaretRight size={16} weight="bold"/>
          </button>
          <Button onClick={()=>setShowScan(true)} className="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white h-10 px-4 hover:opacity-90" data-testid="btn-scan-receipt">
            <Sparkle size={16} weight="bold"/><span className="ml-1.5 font-semibold">Beleg scannen</span>
          </Button>
          <Button onClick={openNew} className="rounded-xl bg-primary text-white h-10 px-5" data-testid="btn-add-expense">
            <Plus size={16} weight="bold"/><span className="ml-1.5 font-semibold">Ausgabe erfassen</span>
          </Button>
          <div className="w-px h-8 bg-slate-200 mx-1"/>
          <button
            onClick={()=>onQuickClick("wash")}
            onContextMenu={(e)=>onQuickContext("wash", e)}
            onMouseDown={()=>startHold("wash")} onMouseUp={cancelHold} onMouseLeave={cancelHold}
            onTouchStart={()=>startHold("wash")} onTouchEnd={cancelHold} onTouchCancel={cancelHold}
            disabled={washBusy==="wash"}
            title={`Waschmaschine +1${washStats?.config?.wash_cost ? ` (${euro(washStats.config.wash_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
            data-testid="btn-quick-wash"
            className="h-10 w-10 rounded-xl border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 flex items-center justify-center transition disabled:opacity-60 relative select-none">
            <WashingMachine size={20} weight="duotone"/>
            {washStats?.today?.wash?.count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-sky-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {washStats.today.wash.count}
              </span>
            )}
          </button>
          <button
            onClick={()=>onQuickClick("dryer")}
            onContextMenu={(e)=>onQuickContext("dryer", e)}
            onMouseDown={()=>startHold("dryer")} onMouseUp={cancelHold} onMouseLeave={cancelHold}
            onTouchStart={()=>startHold("dryer")} onTouchEnd={cancelHold} onTouchCancel={cancelHold}
            disabled={washBusy==="dryer"}
            title={`Trockner +1${washStats?.config?.dryer_cost ? ` (${euro(washStats.config.dryer_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
            data-testid="btn-quick-dryer"
            className="h-10 w-10 rounded-xl border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center transition disabled:opacity-60 relative select-none">
            <Fan size={20} weight="duotone"/>
            {washStats?.today?.dryer?.count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-amber-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {washStats.today.dryer.count}
              </span>
            )}
          </button>
          <button
            onClick={()=>onQuickClick("espresso")}
            onContextMenu={(e)=>onQuickContext("espresso", e)}
            onMouseDown={()=>startHold("espresso")} onMouseUp={cancelHold} onMouseLeave={cancelHold}
            onTouchStart={()=>startHold("espresso")} onTouchEnd={cancelHold} onTouchCancel={cancelHold}
            disabled={washBusy==="espresso"}
            title={`Espresso +1${washStats?.config?.espresso_cost ? ` (${euro(washStats.config.espresso_cost)})` : ""} · Rechtsklick / lang drücken = −1`}
            data-testid="btn-quick-espresso"
            className="h-10 w-10 rounded-xl border border-stone-300 bg-stone-100 text-stone-700 hover:bg-stone-200 flex items-center justify-center transition disabled:opacity-60 relative select-none">
            <Coffee size={20} weight="duotone"/>
            {washStats?.today?.espresso?.count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-stone-700 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {washStats.today.espresso.count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="msb-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Gesamt {monthLabel(month)}</div>
          <div className="text-3xl font-bold mt-2 text-red-600" style={{fontFamily:"'Work Sans',sans-serif"}}>{euro(total)}</div>
          <div className="text-xs text-muted-foreground mt-1">{finalRows.length} {finalRows.length === 1 ? "Ausgabe" : "Ausgaben"}{drafts.length > 0 ? ` · ${drafts.length} Entwurf${drafts.length === 1 ? "" : "e"}` : ""}</div>
        </div>
        <div className="msb-card md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
            <Tag size={14} weight="bold"/> Nach Kategorie
          </div>
          {!summary?.by_category?.length && <div className="text-sm text-muted-foreground py-4">Noch keine Kategorien in diesem Monat.</div>}
          <div className="space-y-2">
            {(summary?.by_category || []).map(c => {
              const pct = total ? Math.round((c.total / total) * 100) : 0;
              return (
                <div key={c.category} className="flex items-center gap-3">
                  <div className={`min-w-[130px] text-xs font-semibold px-2 py-1 rounded-full ${catCls(c.category)}`}>{c.category}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="min-w-[80px] text-right font-mono text-sm">{euro(c.total)}</div>
                  <div className="min-w-[40px] text-right text-xs text-muted-foreground">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* KI-Entwürfe — bitte prüfen und bestätigen */}
      {drafts.length > 0 && (
        <div className="msb-card border-2 border-rose-300 bg-rose-50/50" data-testid="drafts-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Robot size={18} weight="duotone" className="text-rose-600"/>
              <h2 className="font-bold text-rose-900" style={{fontFamily:"'Work Sans',sans-serif"}}>
                KI-Entwürfe · {drafts.length} zu prüfen
              </h2>
            </div>
            <div className="text-xs text-rose-700">
              Belege wurden von der KI gelesen. Bitte kurz kontrollieren und bestätigen oder verwerfen. Erst nach Bestätigung fließen sie in die MwSt- und Steuerberater-Auswertung.
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/60">
                <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="text-left p-2">Datum</th>
                  <th className="text-left p-2">Absender / Anbieter</th>
                  <th className="text-left p-2">Zweck</th>
                  <th className="text-left p-2">Kategorie</th>
                  <th className="text-right p-2">Betrag</th>
                  <th className="text-right p-2">MwSt</th>
                  <th className="text-center p-2">Beleg</th>
                  <th className="text-right p-2">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map(r => (
                  <tr key={r.id} className="border-t border-rose-200 hover:bg-white/60" data-testid={`draft-row-${r.id}`}>
                    <td className="p-2 whitespace-nowrap font-mono text-xs">
                      {r.date ? new Date(r.date).toLocaleDateString("de-DE",{day:"2-digit",month:"2-digit",year:"2-digit"}) : "—"}
                    </td>
                    <td className="p-2">
                      <div className="font-semibold text-sm">{r.vendor || r.gmail_from || "—"}</div>
                      {r.gmail_subject && <div className="text-[11px] text-muted-foreground truncate max-w-[240px]" title={r.gmail_subject}>{r.gmail_subject}</div>}
                    </td>
                    <td className="p-2 text-sm">{r.purpose}</td>
                    <td className="p-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${catCls(r.category)}`}>{r.category}</span>
                    </td>
                    <td className="p-2 text-right font-mono font-bold text-red-700">{euro(r.amount)}</td>
                    <td className="p-2 text-right text-xs font-mono">
                      {(r.mwst_7_amount > 0 || r.mwst_19_amount > 0) ? (
                        <div className="text-slate-600 leading-tight">
                          {r.mwst_7_amount > 0 && <div>7 %: {euro(r.mwst_7_amount)}</div>}
                          {r.mwst_19_amount > 0 && <div>19 %: {euro(r.mwst_19_amount)}</div>}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2 text-center">
                      {r.receipt_file ? (
                        <a href={`${API_BASE}/api/expenses/${r.id}/receipt`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 text-xs font-semibold" data-testid={`draft-receipt-${r.id}`}>
                          <FileText size={14} weight="bold"/> PDF
                        </a>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={()=>openEdit(r)} className="h-8 w-8 rounded-lg hover:bg-white flex items-center justify-center" title="Bearbeiten" data-testid={`draft-edit-${r.id}`}>
                          <PencilSimple size={14} weight="bold"/>
                        </button>
                        <button onClick={()=>confirmDraft(r.id)} className="h-8 px-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-xs hover:bg-emerald-700 flex items-center gap-1" title="Als Ausgabe bestätigen" data-testid={`draft-confirm-${r.id}`}>
                          <CheckCircle size={14} weight="bold"/> OK
                        </button>
                        <button onClick={()=>rejectDraft(r.id)} className="h-8 w-8 rounded-lg bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 flex items-center justify-center" title="Verwerfen" data-testid={`draft-reject-${r.id}`}>
                          <XCircle size={14} weight="bold"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Wiederkehrende Ausgaben */}
      <RecurringExpenses categories={categories} onPosted={load}/>

      {/* 12-Monats-Übersicht */}
      {yearly && (
        <div className="msb-card" data-testid="yearly-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChartBar size={16} weight="bold" className="text-primary"/>
              <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Jahresübersicht {year}</h2>
            </div>
            <div className="text-sm text-muted-foreground">
              Gesamt: <span className="font-bold text-red-600 ml-1">{euro(yearly.year_total)}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={yearly.months} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false}/>
              <XAxis dataKey="label" stroke="#6b7280" fontSize={11}/>
              <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v)=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
              <Tooltip formatter={(v)=>euro(v)} labelFormatter={(l)=>`${l} ${year}`}/>
              <Bar dataKey="total" radius={[6,6,0,0]} onClick={(d)=>setMonth(`${year}-${d.month}`)} cursor="pointer">
                {yearly.months.map((m) => (
                  <Cell key={m.month} fill={month.endsWith(`-${m.month}`) ? "#2d5a3d" : "#f59e0b"}/>
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[11px] text-muted-foreground text-center">Balken anklicken, um einen Monat zu wählen</div>
        </div>
      )}

      {/* Journal */}
      <div className="msb-card p-0 overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Journal · {monthLabel(month)}</h2>
            <div className="text-xs text-muted-foreground">Sortiert nach Datum (neueste zuerst)</div>
          </div>
          {washStats && (washStats.month?.wash?.count > 0 || washStats.month?.dryer?.count > 0 || washStats.month?.espresso?.count > 0) && (
            <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="wash-summary">
              {washStats.month.wash.count > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 border border-sky-200 font-semibold">
                  Waschmaschine · {washStats.month.wash.count}× · {euro(washStats.month.wash.total)}
                </span>
              )}
              {washStats.month.dryer.count > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
                  Trockner · {washStats.month.dryer.count}× · {euro(washStats.month.dryer.total)}
                </span>
              )}
              {washStats.month.espresso.count > 0 && (
                <span className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-800 border border-stone-300 font-semibold">
                  Espresso · {washStats.month.espresso.count}× · {euro(washStats.month.espresso.total)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                <th className="text-left p-3">Datum</th>
                <th className="text-left p-3">Zweck</th>
                <th className="text-left p-3">Kategorie</th>
                <th className="text-right p-3">Betrag</th>
                <th className="text-center p-3" title="Steuer-Anteil (100 % = nur Ferienwohnung · 2/3 = Haus-Gemeinkosten)">Anteil</th>
                <th className="text-right p-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {finalRows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Keine Ausgaben in {monthLabel(month)}.</td></tr>
              )}
              {finalRows.map(r => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30 group" data-testid={`exp-row-${r.id}`}>
                  <td className="p-3 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Calendar size={12} weight="duotone" className="text-slate-400"/>
                      {new Date(r.date).toLocaleDateString("de-DE",{weekday:"short",day:"2-digit",month:"2-digit",year:"numeric"})}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-semibold flex items-center gap-1.5">
                      {r.purpose}
                      {r.receipt_file && (
                        <a href={`${API_BASE}/api/expenses/${r.id}/receipt`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900" title="Beleg ansehen" data-testid={`exp-receipt-${r.id}`}>
                          <Paperclip size={12} weight="bold"/>
                        </a>
                      )}
                    </div>
                    {r.notes && <div className="text-xs text-muted-foreground truncate max-w-md">{r.notes}</div>}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${catCls(r.category)}`}>{r.category}</span>
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-red-600">{euro(r.amount)}</td>
                  <td className="p-3 text-center">
                    <button
                      onClick={async ()=>{ try { await api.patch(`/expenses/${r.id}/toggle-haus-geteilt`); toast.success(r.haus_geteilt ? "Auf 100 % umgestellt" : "Auf 2/3 anteilig umgestellt"); load(); } catch(e){ toast.error("Fehler"); }}}
                      title={r.haus_geteilt ? "Klick: auf 100 % umstellen" : "Klick: auf 2/3 anteilig umstellen (gemeinsame Hauskosten)"}
                      data-testid={`toggle-2-3-${r.id}`}
                      className={`px-2 py-0.5 rounded-full text-[11px] font-bold border transition ${r.haus_geteilt ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200" : "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200"}`}>
                      {r.haus_geteilt ? "2/3" : "100 %"}
                    </button>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={()=>openEdit(r)} className="p-1.5 rounded hover:bg-slate-200" title="Bearbeiten" data-testid={`exp-edit-${r.id}`}>
                        <PencilSimple size={14} weight="bold"/>
                      </button>
                      <button onClick={()=>del(r.id)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Löschen" data-testid={`exp-del-${r.id}`}>
                        <Trash size={14} weight="bold"/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {finalRows.length > 0 && (
                <tr className="border-t-2 border-primary/30 bg-primary/5 font-bold">
                  <td className="p-3" colSpan={3}>Gesamt {monthLabel(month)}</td>
                  <td className="p-3 text-right font-mono text-red-600">{euro(total)}</td>
                  <td/>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {edit && <ExpenseDialog value={edit} categories={categories} onClose={()=>setEdit(null)} onSaved={()=>{setEdit(null); load();}}/>}
      {showScan && <ReceiptScanDialog categories={categories} onClose={()=>setShowScan(false)} onSaved={()=>{ setShowScan(false); load(); }}/>}
    </div>
  );
}
